/**
 * Picks up a new deploy when the app is opened again, instead of whenever iOS
 * feels like it.
 *
 * An app added to the home screen is not relaunched when its icon is tapped: iOS
 * froze it when it was put away and simply resumes it, often for days. Nothing
 * reloads, so nothing notices a new version, and there is no address bar to pull
 * down. So every time the page comes back into view it asks the server what is
 * deployed (`/version.json`, written by the build next to `index.html`) and
 * reloads if that is not this bundle.
 *
 * Four rules keep that from being worse than a stale build:
 *
 * - **Offline asks nothing.** Local play works with no network, and a reload
 *   there would be a reload into nothing. A failed or slow request is treated
 *   the same as offline - never as a reason to reload.
 * - **Not during a game.** On a table, in a lobby or in the tutorial the check
 *   does not run at all - not deferred, not queued for when the player leaves.
 *   The next time the app is opened on any other screen picks the update up.
 * - **Once per version.** If the reload still serves the old page - a CDN edge
 *   that has not caught up - the same version is not tried again this session,
 *   or the app would reload in a loop.
 * - **Not on a workstation.** The dev server has no `version.json`, and a local
 *   build has nothing deployed to compare against.
 *
 * `firebase.json` serves `index.html` with `no-cache` for the other half: a
 * reload that fetched a cached copy of the old page would change nothing.
 */

/**
 * Routes that are a game in progress; the check never runs on these. A campaign
 * level is one. It was missing, so coming back to the app mid-level after a
 * deploy reloaded the player out of the attempt - most likely what lost one
 * player a Zenith they were a move from winning. The map (`/campaign`) is not
 * busy, and stays a place to pick an update up.
 */
const BUSY_ROUTES = [/^\/local\/play/, /^\/online\/play\//, /^\/online\/lobby\//, /^\/tutorial/, /^\/campaign\/play\//];
/** A resume is often a glance; don't ask the server more than this often. */
const MIN_CHECK_GAP_MS = 60_000;
/** A version check is not worth waiting on. Longer than this and it didn't happen. */
const CHECK_TIMEOUT_MS = 4_000;
const TRIED_KEY = "fiveout:reloadedFor";

/**
 * Whether to reload, without the browser. `latest` is null when the server
 * could not be asked.
 */
export function shouldReload(input: { current: string; latest: string | null; alreadyTriedFor: string | null }): boolean {
  const { current, latest, alreadyTriedFor } = input;
  return !!latest && latest !== current && latest !== alreadyTriedFor;
}

/** The HashRouter path of a `location.hash`: `#/local/play?x=1` is `/local/play`. */
export function routeOf(hash: string): string {
  const path = hash.replace(/^#/, "").split("?")[0];
  return path || "/";
}

export function isBusyRoute(path: string): boolean {
  return BUSY_ROUTES.some((r) => r.test(path));
}

async function fetchDeployedVersion(): Promise<string | null> {
  if (navigator.onLine === false) return null;
  const abort = new AbortController();
  const timer = window.setTimeout(() => abort.abort(), CHECK_TIMEOUT_MS);
  try {
    const res = await fetch("/version.json", { cache: "no-store", signal: abort.signal });
    if (!res.ok) return null;
    const body = (await res.json()) as { version?: unknown };
    return typeof body.version === "string" ? body.version : null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

function readTried(): string | null {
  try {
    return sessionStorage.getItem(TRIED_KEY);
  } catch {
    return null;
  }
}

function reloadFor(version: string): void {
  try {
    sessionStorage.setItem(TRIED_KEY, version);
  } catch {
    // Without the guard a stale edge could loop; better to stay on this build.
    return;
  }
  window.location.reload();
}

/**
 * Call once at startup with `version.ts`'s values - passed in rather than
 * imported so the rules above stay testable outside a Vite build.
 */
export function startFreshnessCheck(appVersion: string, appEnv: string): void {
  if (appEnv === "local") return;

  let lastCheck = 0;

  const check = async () => {
    if (document.visibilityState !== "visible") return;
    if (isBusyRoute(routeOf(window.location.hash))) return;
    const now = Date.now();
    if (now - lastCheck < MIN_CHECK_GAP_MS) return;
    lastCheck = now;
    const latest = await fetchDeployedVersion();
    // The player may have walked into a game while the request was out.
    if (isBusyRoute(routeOf(window.location.hash))) return;
    if (latest && shouldReload({ current: appVersion, latest, alreadyTriedFor: readTried() })) reloadFor(latest);
  };

  document.addEventListener("visibilitychange", () => void check());
  // A page restored from the back/forward cache fires this and not a visibility change.
  window.addEventListener("pageshow", (e) => {
    if (e.persisted) void check();
  });
  // A cold launch can still have booted a page cached before the headers changed.
  void check();
}
